/**
 * MaterialListDialog: More -> Material List (IDI-105).
 *
 * Renders the ruled layout from quote-builder/materialList.js (area bands,
 * sub-area in Items, no money, Qty on Hand unclamped). Download fetches the
 * server-rendered PDF; Send emails that same PDF. The rows sent to the server
 * are the rows on screen, so the modal, PDF and email cannot disagree.
 *
 * Recipients default to the dealer's branch order email (dealer directory
 * `branch_order_email`) and the signed-in user.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '../../ui/Dialog';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table';
import { Button, Input, Textarea } from '../../ui';
import { useToast } from '../../ui/Toast';
import apiClient from '../../../services/api';
import { buildMaterialList, flattenMaterialList } from '../../../quote-builder/materialList';

const fmtQty = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(2));
const fmtSqft = (v) => Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

export function parseRecipients(text) {
  return String(text || '')
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const EMAIL_RE = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;

function downloadBase64Pdf(b64, filename) {
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'Material-List.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function MaterialListDialog({
  open,
  onClose,
  quoteId,
  lineItems,
  catalog,
  accessoryCatalog,
  title,
  canSend = true,
}) {
  const toast = useToast();
  const bands = useMemo(
    () => buildMaterialList(lineItems, { catalog, accessoryCatalog }),
    [lineItems, catalog, accessoryCatalog],
  );
  const rows = useMemo(() => flattenMaterialList(bands), [bands]);

  const [mode, setMode] = useState('view'); // 'view' | 'send'
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setMode('view');
      setError('');
      setMessage('');
    }
  }, [open]);

  useEffect(() => {
    if (!open || mode !== 'send' || !quoteId) return undefined;
    let live = true;
    apiClient.getMaterialListRecipients(quoteId)
      .then((d) => {
        if (!live) return;
        setTo((prev) => prev || [d?.branchOrderEmail, d?.userEmail].filter(Boolean).join(', '));
      })
      .catch(() => {});
    return () => { live = false; };
  }, [open, mode, quoteId]);

  const recipients = parseRecipients(to);
  const badRecipient = recipients.find((r) => !EMAIL_RE.test(r));

  const handleDownload = async () => {
    if (!quoteId) return;
    setBusy(true);
    setError('');
    try {
      const d = await apiClient.materialList(quoteId, rows);
      if (!d?.pdf_base64) throw new Error('No PDF returned');
      downloadBase64Pdf(d.pdf_base64, d.filename);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not generate the material list PDF.');
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async () => {
    if (!quoteId || recipients.length === 0 || badRecipient) return;
    setBusy(true);
    setError('');
    try {
      const d = await apiClient.materialList(quoteId, rows, { to: recipients, message });
      const sent = d?.sentTo || [];
      const failed = d?.failed || [];
      if (failed.length) {
        toast.warning(`Material list sent to ${sent.join(', ')}. Failed: ${failed.join(', ')}`);
      } else {
        toast.success(`Material list sent to ${sent.join(', ')}`);
      }
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || 'The material list could not be sent.');
    } finally {
      setBusy(false);
    }
  };

  const noQuote = !quoteId;

  return (
    <Dialog open={open} onClose={onClose} size="report">
      <DialogHeader onClose={onClose}>{mode === 'send' ? 'Send Material List' : 'Material List'}</DialogHeader>
      <DialogBody>
        {mode === 'view' ? (
          <div className="text-sm space-y-3" data-testid="material-list">
            {title && <p style={{ color: 'var(--color-text-muted)' }}>{title}</p>}
            {bands.length === 0 ? (
              <p style={{ color: 'var(--color-text-muted)' }}>No materials on this quote yet. Add line items to build the list.</p>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Items</TableHeader>
                    <TableHeader>Product</TableHeader>
                    <TableHeader align="right">Square Footage</TableHeader>
                    <TableHeader>R-Value</TableHeader>
                    <TableHeader align="right">Item Qty</TableHeader>
                    <TableHeader align="right">Qty on Hand</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bands.map((band) => (
                    <React.Fragment key={band.areaLabel}>
                      <TableRow>
                        <TableCell className="font-semibold" colSpan={6} style={{ backgroundColor: 'var(--color-surface-sunken)', color: 'var(--color-text-primary)' }}>
                          {band.areaLabel}
                        </TableCell>
                      </TableRow>
                      {band.rows.map((r, i) => (
                        <TableRow key={`${band.areaLabel}-${i}`}>
                          <TableCell>{r.item || '—'}</TableCell>
                          <TableCell>{r.product}</TableCell>
                          <TableCell align="right">{fmtSqft(r.sqft)}</TableCell>
                          <TableCell>{r.rValue || '—'}</TableCell>
                          <TableCell align="right">{fmtQty(r.qty)}</TableCell>
                          <TableCell
                            align="right"
                            style={r.qtyOnHand !== null && r.qtyOnHand < 0 ? { color: 'var(--color-status-error-text)' } : undefined}
                          >
                            {r.qtyOnHand === null ? '—' : fmtQty(r.qtyOnHand)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            )}
            {noQuote && (
              <p style={{ color: 'var(--color-text-muted)' }}>Download and Send are available once the quote has saved.</p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <Input
              label="To"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="branch@example.com, you@example.com"
              aria-label="Recipients"
            />
            {badRecipient && (
              <p className="text-sm" style={{ color: 'var(--color-status-error-text)' }}>Not a valid email address: {badRecipient}</p>
            )}
            <Textarea
              label="Message (optional)"
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={2000}
            />
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
              The material list PDF ({rows.length} {rows.length === 1 ? 'row' : 'rows'}) is attached.
            </p>
          </div>
        )}
        {error && <p className="text-sm mt-3" role="alert" style={{ color: 'var(--color-status-error-text)' }}>{error}</p>}
      </DialogBody>
      <DialogFooter>
        {mode === 'view' ? (
          <>
            <Button variant="secondary" onClick={onClose}>Close</Button>
            <Button variant="secondary" onClick={handleDownload} disabled={busy || noQuote}>Download PDF</Button>
            {canSend && (
              <Button onClick={() => setMode('send')} disabled={busy || noQuote || rows.length === 0}>Send</Button>
            )}
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setMode('view')} disabled={busy}>Back</Button>
            <Button onClick={handleSend} disabled={busy || recipients.length === 0 || Boolean(badRecipient)}>
              {busy ? 'Sending…' : 'Send'}
            </Button>
          </>
        )}
      </DialogFooter>
    </Dialog>
  );
}
